import { LAYOUTS, MODULE_SETTINGS, TEACHER_ROLES } from "shared/constants";
import type { AppRouteRecordRaw } from "shared/types";
import { CONVERSATION_ROUTE_NAMES } from "conversations/routes/routeNames";

/**
 * Gesprekken route fragment for /my-students/:mentorClass/conversations/create.
 * Import and spread into the mentorClass.routes.ts children array.
 */
export const myStudentsConversationsFragment: AppRouteRecordRaw[] = [
  {
    // A child of the class, not a top-level path: planning a gesprek from a mentorklas
    // stays inside that class's screen, under its header and tabs.
    path: "conversations/create",
    name: CONVERSATION_ROUTE_NAMES.MENTOR_CLASS_INVITATION_CREATE,
    component: () =>
      import("classroom/pages/MentorClassConversationInvitationCreatePage.vue"),
    meta: {
      title: "Gesprek inplannen",
      roleRequired: TEACHER_ROLES,
      settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
      layout: LAYOUTS.MAIN_NO_WRAPPER,
      // The class page skips its header and tabs for this route, the way the tasks tree
      // hides its submenu on `/tasks/manage/scheduled/create`: the form brings its own
      // heading and is left by saving or cancelling, not by switching tabs.
      hideSubmenu: true,
    },
  },
  {
    // The ingepland gesprek and its edit screen, as children of the mentorklas so the teacher
    // stays inside it; the same pages the standalone routes render. Under their own
    // `invitations` segment, as `/conversations/invitation` sits below `/conversations`
    // standalone: a bare id below `conversations/` is a gesprek.
    path: "conversations/invitations/:invitationId(\\d+)",
    name: CONVERSATION_ROUTE_NAMES.MENTOR_CLASS_INVITATION_DETAIL,
    component: () =>
      import("conversations/pages/TeacherConversationInvitationDetailPage.vue"),
    meta: {
      title: "Gesprek",
      roleRequired: TEACHER_ROLES,
      settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
      layout: LAYOUTS.MAIN_NO_WRAPPER,
      hideSubmenu: true,
    },
  },
  {
    path: "conversations/invitations/:invitationId(\\d+)/edit",
    name: CONVERSATION_ROUTE_NAMES.MENTOR_CLASS_INVITATION_EDIT,
    component: () =>
      import("conversations/pages/TeacherConversationInvitationEditPage.vue"),
    meta: {
      title: "Gesprek bewerken",
      roleRequired: TEACHER_ROLES,
      settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
      layout: LAYOUTS.MAIN_NO_WRAPPER,
      hideSubmenu: true,
    },
  },
  {
    // One gesprek — a gespreksverslag, or a leerling's gesprek under an invitation — opened
    // from this mentorklas. Digits only, so `conversations/create` and `conversations/invitations`
    // keep their own routes.
    path: "conversations/:conversationId(\\d+)",
    name: CONVERSATION_ROUTE_NAMES.MENTOR_CLASS_DETAIL,
    component: () =>
      import("conversations/pages/TeacherConversationDetailPage.vue"),
    meta: {
      title: "Gesprek",
      roleRequired: TEACHER_ROLES,
      settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
      layout: LAYOUTS.MAIN_NO_WRAPPER,
      hideSubmenu: true,
    },
  },
];
